import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/assets/logo.png";
import NavBar from "../Client/components/Navigationbar";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const API_URL = import.meta.env.VITE_API_URL;

function evaluatePassword(pw) {
  const lengthOK = pw.length >= 6;
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);

  const score =
    (lengthOK ? 1 : 0) +
    (hasLower ? 1 : 0) +
    (hasUpper ? 1 : 0) +
    (hasNumber ? 1 : 0) +
    (hasSpecial ? 1 : 0);

  let label = "";
  let color = "";

  if (!pw) {
    label = "";
    color = "";
  } else if (score <= 2) {
    label = "Weak";
    color = "bg-red-500";
  } else if (score === 3) {
    label = "Moderate";
    color = "bg-yellow-500";
  } else {
    label = "Strong";
    color = "bg-green-500";
  }

  return { score, label, color };
}

function getPasswordMissingRules(pw) {
  const v = pw || "";
  const missing = [];

  if (v.length < 6) missing.push("At least 6 characters");
  if (!/[a-z]/.test(v)) missing.push("At least 1 lowercase letter (a-z)");
  if (!/[A-Z]/.test(v)) missing.push("At least 1 uppercase letter (A-Z)");
  if (!/(\d|[^A-Za-z0-9])/.test(v))
    missing.push("At least 1 number (0-9) or special symbol (!@#$...)");

  return missing; // [] means valid
}

function isValidEmail(v) {
  const email = (v || "").trim();

  // simple, reliable-enough email check (no spaces, has @, has dot after @)
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function SdaRegister() {
  const navigate = useNavigate();

  // Form Fields
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailValid, setEmailValid] = useState(null); // null | true | false
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
  });
  const [passwordMissing, setPasswordMissing] = useState([]); // ✅ ADD
  const [passwordError, setPasswordError] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordStrength(evaluatePassword(value));

    const missing = getPasswordMissingRules(value);
    setPasswordMissing(missing);

    // if password is valid, remove the red message
    if (missing.length === 0) {
      setPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setPasswordError("");

    if (
      !fullName ||
      !username ||
      !email ||
      !phone ||
      !password ||
      !confirmPassword
    ) {
      setError("Please fill in all fields.");
      return;
    }

    // password rules (STRICT)
    const missing = getPasswordMissingRules(password);
    setPasswordMissing(missing);

    if (missing.length > 0) {
      setPasswordError(""); // we show the bullet list instead
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: fullName,
        username: username,
        email,
        phone,
        password,
        role: "SDA Owner",
        status: "Pending",
      };

      // ✅ use Flask backend URL (port 5000)
      const res = await fetch(`${API_URL}/api/public/register_sda_owner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          username,
          email,
          phone,
          password,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        // ignore JSON parse error if response isn't JSON
      }

      if (!res.ok) {
        setError(
          (data && data.message) ||
            `Request failed (${res.status}). Please try again.`
        );
        return;
      }

      console.log("Register success:", data);
      setSuccess(
        (data && data.message) ||
          "Registration submitted. Please wait for admin approval before signing in."
      );
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <NavBar logo={Logo} />

      {/* Main content */}
      <main>
        <div className="max-w-5xl mx-auto flex justify-center">
          <Card className="w-full max-w-xl shadow-none border-0">
            <CardHeader className="pb-4 text-center">
              <CardTitle className="text-2xl font-semibold">
                Create your partner account
              </CardTitle>
              <CardDescription className="mt-2 text-gray-700">
                Enter your details to proceed with the registration.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Name</Label>
                  <Input
                    id="firstName"
                    placeholder="e.g. John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Username</Label>
                  <Input
                    id="lastName"
                    placeholder="e.g. john_doe123"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmail(v);

                      // live validate once user started interacting
                      if (emailTouched) {
                        setEmailValid(isValidEmail(v));
                      }
                    }}
                    onBlur={() => {
                      setEmailTouched(true);
                      setEmailValid(isValidEmail(email));
                    }}
                    className={`${
                      emailTouched && emailValid === true
                        ? "border-green-500 focus-visible:ring-green-500"
                        : emailTouched && emailValid === false
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                    required
                  />
                  {emailTouched && emailValid === false && (
                    <p className="text-xs text-red-500 mt-1">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>

                {/* 👇 Phone number moved here (below Email) */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone number</Label>
                  <PhoneInput
                    country={"au"} // default to Australia (change if needed)
                    value={phone}
                    onChange={(value) =>
                      setPhone(value ? `+${value.replace(/^\+/, "")}` : "")
                    }
                    enableSearch={true}
                    countryCodeEditable={false}
                    inputProps={{
                      name: "phone",
                      required: true,
                    }}
                    containerClass="w-full"
                    inputClass="!w-full !h-10 !text-sm !border !border-input !bg-background !rounded-md !px-3 !py-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                  />

                  {/* strength bar */}
                  {passwordStrength.label && (
                    <div className="mt-1">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all`}
                          style={{
                            width: `${(passwordStrength.score / 4) * 100}%`,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        Password strength:{" "}
                        <span className="font-medium">
                          {passwordStrength.label}
                        </span>
                      </p>
                    </div>
                  )}

                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 6 characters and include upper &amp; lower
                    case letters and at least one number or special symbol.
                  </p>

                  {/* Live missing requirements (disappears when complete) */}
                  {password.length > 0 && passwordMissing.length > 0 && (
                    <div className="mt-2 text-xs text-red-500">
                      <p className="mb-1">Password must include:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        {passwordMissing.map((rule) => (
                          <li key={rule}>{rule}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Keep this for other errors (like mismatch) */}
                  {passwordError && (
                    <p className="mt-2 text-xs text-red-500">{passwordError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError(""); // clears mismatch message while typing
                    }}
                    required
                  />
                </div>

                {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
                {success && (
                  <p className="text-sm text-green-600 mt-1">{success}</p>
                )}

                <Button
                  type="submit"
                  className="mt-4 w-full bg-pink-600 hover:bg-pink-700"
                  disabled={loading}>
                  {loading ? "Signing up..." : "Sign up"}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex justify-center mt-4">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-pink-600 font-medium hover:underline">
                  Sign in here
                </button>
              </p>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default SdaRegister;
